/**
 * The phone shell (ROADMAP "The phone branch", item 2).
 *
 * A phone LAYOUT inside the same app — its own chrome, the same pages and data.
 * Top bar with the wordmark, one column of content, three bottom tabs within
 * thumb reach: Home, Research and Blog. A top-left menu lists the desktop
 * sidebar's items in its order, plus the theme toggle.
 * No side rail, no currency selector, no floating chat (that one is gated in
 * App.tsx, where it mounts).
 *
 * Pages must not import this or `Layout` — the same page renders inside both,
 * which is what keeps the phone document equal to the desktop one for the
 * mobile-first index.
 */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
  Avatar, Box, Button, Divider, Drawer, IconButton, Link, List, ListItemButton, ListItemIcon,
  ListItemText, ListSubheader, Menu, MenuItem, Typography,
} from '@mui/material';
import {
  DarkMode, Home as HomeIcon, InstallMobile, LightMode, Menu as MenuIcon, Newspaper, Search,
} from '@mui/icons-material';
import { useHistoryMigration } from './hooks/useHistoryMigration';
import { useAuth } from './contexts/AuthContext';
import { useLoginDialog } from './contexts/LoginDialogContext';
import { useTheme as useAppTheme } from './contexts/ThemeContext';
import UserAvatarMUI from './components/UserAvatarMUI';
import NotificationBell from './components/levelup/NotificationBell';
import analyticsService, { ANALYTICS_SOURCES } from './services/analytics';
import { PHONE_TABS, activePhoneTab, handsOverToDesktop, isPhoneFullscreenRoute, matchesPattern } from './phone/phoneRoutes';
import { navRailItems } from './Layout';
import DesktopHandover from './phone/DesktopHandover';
import PortfoliosPhone from './phone/PortfoliosPhone';
import { PHONE_TAB_BAR_HEIGHT, PHONE_TOP_BAR_HEIGHT } from './phone/phoneMetrics';
import { installRoute, onInstallAvailabilityChange } from './phone/installPrompt';
import { OPEN_INSTALL_SHEET_EVENT } from './phone/PhoneInstallSheet';


const TAB_ICONS: Record<string, React.ReactElement> = {
  '/': <HomeIcon />,
  '/research': <Search />,
  '/blog': <Newspaper />,
};

const FOOTER_LINKS = [
  { label: 'Privacy', path: '/privacy' },
  { label: 'Terms', path: '/terms' },
  { label: 'Sitemap', path: '/sitemap' },
];

const PhoneLayout: React.FC = () => {
  const location = useLocation();
  const activeTab = activePhoneTab(location.pathname);

  // Layout runs this too: a returning signed-in reader still needs their
  // session history migrated, whichever shell they land in.
  useHistoryMigration();

  // Signing in works on a phone (owner, 2026-09-28): a shared report asks for
  // it, and a reader who signs in here is recognised on their desktop later.
  const { user, signOut } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  // "Install app" appears only where installing is actually possible: Chrome's
  // offer is in hand, or this is iOS Safari; never when already installed.
  const [canInstall, setCanInstall] = useState(() => installRoute() !== 'none');
  useEffect(() => onInstallAvailabilityChange(() => setCanInstall(installRoute() !== 'none')), []);
  const { effectiveTheme, toggleTheme } = useAppTheme();

  const handleThemeToggle = async () => {
    const current = effectiveTheme;
    await toggleTheme();
    analyticsService.trackThemeToggled(current, current === 'light' ? 'dark' : 'light');
  };

  // The menu IS the desktop sidebar: same items, same order (owner,
  // 2026-09-28). Whether an item needs a desktop is not written here — it is
  // the hand-over rule itself, so the label cannot disagree with the page.
  const menuLink = (item: typeof navRailItems[number]) => (
    <ListItemButton
      key={item.path}
      component={RouterLink}
      to={item.path}
      selected={location.pathname === item.path}
      onClick={() => {
        analyticsService.trackNavigation(item.label, ANALYTICS_SOURCES.PHONE_MENU);
        setDrawerOpen(false);
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} />
      {handsOverToDesktop(item.path) && (
        <Typography variant="caption" sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.75 }}>
          Desktop
        </Typography>
      )}
    </ListItemButton>
  );

  const handleSignOut = async () => {
    setMenuAnchor(null);
    try {
      analyticsService.trackSignOut(ANALYTICS_SOURCES.PHONE_TABS);
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // A full-screen route (the assistant) draws its own header and has no tabs:
  // the composer sits on the keyboard, as in any messaging app (owner pick 1b).
  if (isPhoneFullscreenRoute(location.pathname)) {
    return (
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Outlet />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          height: PHONE_TOP_BAR_HEIGHT,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton edge="start" aria-label="Menu" onClick={() => setDrawerOpen(true)} sx={{ color: 'text.primary' }}>
            <MenuIcon />
          </IconButton>
          <Link
            component={RouterLink}
            to="/"
            underline="none"
            aria-label="IndexAlpha home"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
          >
            <Avatar src="/logo192.png" alt="" sx={{ width: 28, height: 28, bgcolor: 'common.white' }} />
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              IndexAlpha
            </Typography>
          </Link>
        </Box>

        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Signed-in only, as on desktop: notifications are per account. */}
            <NotificationBell />
            <IconButton aria-label="Account" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
              <UserAvatarMUI size="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
            </Menu>
          </Box>
        ) : (
          <Button
            onClick={() => openLoginDialog(ANALYTICS_SOURCES.PHONE_TABS)}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.primary' }}
          >
            Sign in
          </Button>
        )}
      </Box>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="nav" aria-label="Menu" sx={{ width: 280, pt: 'env(safe-area-inset-top, 0px)' }}>
          <List>
            {navRailItems.map(menuLink)}
          </List>
          <Divider />
          <List subheader={<ListSubheader disableSticky>Appearance</ListSubheader>}>
            <ListItemButton onClick={handleThemeToggle}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {effectiveTheme === 'light' ? <DarkMode /> : <LightMode />}
              </ListItemIcon>
              <ListItemText primary={effectiveTheme === 'light' ? 'Dark mode' : 'Light mode'} />
            </ListItemButton>
          </List>
          {canInstall && (
            <>
              <Divider />
              <List>
                <ListItemButton
                  onClick={() => {
                    setDrawerOpen(false);
                    window.dispatchEvent(new Event(OPEN_INSTALL_SHEET_EVENT));
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><InstallMobile /></ListItemIcon>
                  <ListItemText primary="Install app" secondary="Add IndexAlpha to your home screen" />
                </ListItemButton>
              </List>
            </>
          )}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: `${PHONE_TOP_BAR_HEIGHT}px`,
          // Clear the fixed tab bar, and the home indicator beneath it.
          pb: `calc(${PHONE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {handsOverToDesktop(location.pathname)
            ? // Signed in, the Risk Analysis page lists what you have saved, to
              // read here (owner pick 5a). Signed out — and for every crawler —
              // it stays the explainer and the hand-over.
              user && matchesPattern('/portfolio/risk-assessment', location.pathname)
              ? <PortfoliosPhone />
              : <DesktopHandover />
            : <Outlet />}
        </Box>

        <Box component="footer" sx={{ px: 2, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            {FOOTER_LINKS.map((l) => (
              <Link
                key={l.path}
                component={RouterLink}
                to={l.path}
                underline="hover"
                sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
              >
                {l.label}
              </Link>
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
            © 2026 IndexAlpha Inc. Market data updated daily. Not financial advice.
          </Typography>
        </Box>
      </Box>

      <Box
        component="nav"
        aria-label="Primary"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          display: 'flex',
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {PHONE_TABS.map((tab) => {
          const active = activeTab === tab.path;
          return (
            <Link
              key={tab.path}
              component={RouterLink}
              to={tab.path}
              underline="none"
              aria-current={active ? 'page' : undefined}
              onClick={() => analyticsService.trackNavigation(tab.label, ANALYTICS_SOURCES.PHONE_TABS)}
              sx={{
                flex: 1,
                height: PHONE_TAB_BAR_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                color: active ? 'text.primary' : 'text.secondary',
                fontWeight: active ? 700 : 500,
                fontSize: '0.75rem',
              }}
            >
              {TAB_ICONS[tab.path]}
              {tab.label}
            </Link>
          );
        })}
      </Box>
    </Box>
  );
};

export default PhoneLayout;
